import React from "react";
import { useState, useEffect, useRef } from 'react';
import "./classes/SidebarQuickForm.css";
import { Helmet } from "react-helmet";
import { configData } from "../config";
import { getGlobalValue, subscribeGlobalContent } from './PageEditor/globalContentStore';
import PhoneInput, {
    formatPhoneNumber,
    formatPhoneNumberIntl,
    isPossiblePhoneNumber,
    isValidPhoneNumber,getCountryCallingCode
} from 'react-phone-number-input';
import Select from 'react-dropdown-select';
import ReCAPTCHA from "react-google-recaptcha";
import TextField from "./atomic/molecules/TextField";
import SelectField from "./atomic/molecules/SelectField";
import PhoneField from "./atomic/molecules/PhoneField";
import ConsentCheckbox from "./atomic/molecules/ConsentCheckbox";
import { useQuickApp, submitQuickAppStep1, isBlockedEmail, QUICK_APP_MESSAGES } from "./services/QuickAppService";
import { openQuickAppPopup } from "./QuickAppPopup";

// Degree pages with the general quick form in their right column on the live site. Their leads
// are filed as "degree" leads under the page's node id, as on the Drupal form.
const DEGREE_PAGES = [
    274987, // /masters-professional-studies-sports-management-degree (Fort Hays State)
    80442, // /sports-management-mba-degree (Concordia University Chicago)
];
export const isDegreePage = (page) => DEGREE_PAGES.includes(Number(page?.nid));

// Course pages outside /courses (the apprenticeships) and the degree pages: the live site shows
// its general quick form there, in the right column (in place of a course's own form in the left one)
export const hasGeneralQuickForm = (page) =>
    (page?.type === "course" && !(page?.url_slug || "").startsWith("courses/")) || isDegreePage(page);

const SidebarQuickForm = ({ className = "",page  }) => {

    const [isOpen, setIsOpen] = useState(false);
    const app_url = configData.APP_URL;
    const recaptchaRef = useRef(null);
    const sitekey = configData.GOOGLE_RECAPTCHA_SITE_KEY;

    const initialForm = {
        fname:'',
        lname: '',
        email: '',
        phone: `+${getCountryCallingCode('US')}`,
        fteam:'',
        fcity:'',
        fcountry:'United States',
        fstate:'',
        fprovince:'',
        field_text_me_with_news_offers:'1',
        sku:'',
        page_type:'',
        showStateField: true,
        showProvinceField: false,
        pageURL: window.location.href,
    };

    const [formData, setFormData] = useState(initialForm);
    const [countriesList, setCountriesList] = useState([]);
    const [statelist, setStateList] = useState({});
    const [errors, setErrors] = useState({});
    const [content, setContent] = useState({});
    const [saving, setSaving] = useState(false);
    // The form is step 1 of the application: once it is saved, steps 2 and 3 follow in a popup
    // and every quick form on the page shows where the application stands instead of its fields
    const application = useQuickApp();
    // Bumped when Global Course Content is hydrated or an admin saves, so the
    // shared marketing copy below rebuilds with the live values.
    const [gccVersion, setGccVersion] = useState(0);
    useEffect(() => subscribeGlobalContent(() => setGccVersion((v) => v + 1)), []);

    const displayMode = content?.display || "sidebar";
    useEffect(() => {
    if (page?.field_application_key_name) {
        setFormData(prev => ({
            ...prev,
            sku: page.field_application_key_name,
            page_type:page.type
        }));
    }
     if (page?.course_name) {
     setContent({
        // Marketing microcopy is shared across every course page — resolved
        // from Global Course Content (with the same defaults as fallback). Only
        // the heading stays course-specific: it interpolates the course name.
        eyebrow: getGlobalValue('quick_form_eyebrow'),
        heading: "Learn More About Becoming "+ (/^[aeiou]/i.test(page?.course_name) ? "an " : "a ") + page?.course_name +" Student",
        description: getGlobalValue('quick_form_description'),
        ctaText: getGlobalValue('quick_form_cta_text'),
        footer: getGlobalValue('quick_form_footer'),
        display: "sidebar",
    });
}
    // The general form: the lead is still filed under the course, only the wording differs.
    // It has no "Text me with news and offers" box on the live site, so nobody is signed up
    // for texts from it.
    if (hasGeneralQuickForm(page)) {
        setFormData(prev => ({
            ...prev,
            field_text_me_with_news_offers: 0,
            ...(isDegreePage(page) ? { sku: String(page.nid), page_type: "degree" } : {}),
        }));
        setContent({
            eyebrow: getGlobalValue('quick_form_eyebrow'),
            heading: "Learn More About Becoming a SMWW Student",
            description: "We want to help you find the right course for your career. Fill out the form below and an SMWW Career Counselor will follow up directly with you.",
            ctaText: getGlobalValue('quick_form_cta_text'),
            footer: "The SMWW Team will contact you within one business day.",
            display: "sidebar",
        });
    }
    // Conference pages: the lead is filed under the conference's title, as on the Drupal form
    if (page?.type === "conferences") {
        setFormData(prev => ({
            ...prev,
            sku: page.title,
            page_type: "conference"
        }));
        setContent({
            eyebrow: getGlobalValue('quick_form_eyebrow'),
            heading: "Learn More About Attending This Conference",
            description: "Fill out the form below and an SMWW Career Counselor will follow up directly with you.",
            ctaText: getGlobalValue('quick_form_cta_text'),
            footer: "The SMWW Team will contact you within one business day.",
            display: "sidebar",
        });
    }
    }, [page, gccVersion]);
    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;

        if (name === 'fcountry') {
            setFormData(prev => ({
                ...prev,
                [name]: value,
                showProvinceField: !(value === 'United States' || value === 'Canada'),
                showStateField: (value === 'United States' || value === 'Canada'),
                fprovince: (value === 'United States' || value === 'Canada') ? '' : prev.fprovince,
                fstate: !(value === 'United States' || value === 'Canada') ? '' : prev.fstate,
            }));
        } else if (type === 'checkbox') {
            if (name === "field_text_me_with_news_offers") {
                setFormData(prev => ({ ...prev, [name]: checked ? 1 : 0 }));
            } else {
                setFormData(prev => ({ ...prev, [name]: checked }));
            }
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    useEffect(() => {
   
    }, []);

    useEffect(() => {
        retrieveCountriesList();
        retrieveStateList();
    }, []);

    async function retrieveCountriesList() {
        try {
        
            const countrieslistresponse = await fetch(app_url + 'smwwcountries/getcountrieslist', {
                method: "GET",
                headers: {
                    'Content-Type': 'application/json',
            },
            });
            const countrieslistdata = await countrieslistresponse.json();
            const countrieslist = {};
            Object.keys(countrieslistdata.data.result).forEach(function(key) {
                countrieslist[key] = countrieslistdata.data.result[key];
            });
            setCountriesList(countrieslist);
        } catch (error) {
            console.error(error);
        }finally {
            
        }
    }

    async function retrieveStateList() {
        const statelistresponse = await fetch(
            app_url+'smwwstates/getstatelist',
            {
                method: "GET",
                headers: {
                    'Content-Type': 'application/json',
                },
            }
        )
        const statelistdata = await statelistresponse.json();
        const statelist = {};
        Object.keys(statelistdata.data.result).forEach(function(key) {
            statelist[key] = statelistdata.data.result[key];
        });
        setStateList(statelist);
    }

    const validate = (data) => {
        const newErrors = {};
        // const recaptchaValue = inputRefs.current['recaptcha'].getValue();
        const recaptchaValue = recaptchaRef.current.getValue();
        if (!data.fname.trim()) {
            newErrors.fname = 'First name is required.';
        }
        if (!data.lname.trim())  { 
            newErrors.lname = 'Last name is required.';
        }

        if (!data.email.trim()) {
            newErrors.email = 'Email is required.';
        } else if (!/\S+@\S+\.\S+/.test(data.email)) {
            newErrors.email = 'Please enter a valid email address';
        } else if (isBlockedEmail(data.email)) {
            newErrors.email = QUICK_APP_MESSAGES.email_blocked;
        }
        if (!data.phone || data.phone.trim() === '') {
            newErrors.phone = 'Phone number is required.';
        } else {
            const digitsOnly = data.phone.replace(/\D/g, '');

            try {
                let codeLength = 0;

                if (formData.PrimaryPhoneCountry) {
                    codeLength = getCountryCallingCode(formData.PrimaryPhoneCountry).length;
                } else {
                    const match = digitsOnly.match(/^\d{1,3}/);
                    if (match) codeLength = match[0].length;
                }

                const numberWithoutCode = digitsOnly.slice(codeLength);

                if (!numberWithoutCode || numberWithoutCode.length === 0) {
                    console.log('Only country code entered');
                    newErrors.phone = 'Please enter your phone number';
                    data.phone = '';
                } else if (!isValidPhoneNumber(data.phone)) {
                    newErrors.phone = 'Invalid Phone Number';
                }

            } catch {
                newErrors.phone = 'Invalid Phone Number';
            }
        }

        if (!data.fteam.trim())  { 
            newErrors.fteam = 'Please tell us your favorite team.';
        }

        if (!data.fcity.trim())  { 
            newErrors.fcity = 'City is required.';
        }

        if (!data.fcountry) {
            newErrors.fcountry = 'Please select a country.';
        } else if (data.fcountry === 'United States' || data.fcountry === 'Canada') {
            // "-" is "Outside the US or Canada", which is not a state of the country chosen
            if (!data.fstate || data.fstate === '-') {
                newErrors.fstate = 'Please select a state.';
            }
        } else {
           /* if (!data.fprovince.trim()) {
                newErrors.fprovince = 'Please enter your province.';
            } */
        }
         if (!recaptchaValue) {
            newErrors.recaptcha = 'Please verify the reCAPTCHA.';
        } 
        return newErrors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (saving) return;

        const formErrors = validate(formData);

        if (Object.keys(formErrors).length > 0) {
            setErrors(formErrors);
        } else {
            setErrors({});
            setSaving(true);
            // Step 1 of the application (application/step1), as the Drupal quick form posts it
            const saved = await submitQuickAppStep1(formData, countriesList);
            setSaving(false);
            if (saved) {
                // Optional: reset form
                setFormData(initialForm);
                openQuickAppPopup();
            } else {
                setErrors({ save: QUICK_APP_MESSAGES.save });
            }
        }
    };


const formContent = (
    <>
        {application ? (
            application.stage === 4 ? (
            <div className="ctct-form-success">
                <h2 className="smww-heading">{getGlobalValue('quick_form_success_heading')}</h2>
                <p className="ctct-form-text">{getGlobalValue('quick_form_success_message')}</p>
            </div>
            ) : (
            <div className="sqa-resume">
                <p>Thank you, {application.state.first_name}! You’re one step in.</p>
                <button type="button" className="sqa-btn" onClick={openQuickAppPopup}>Finish your application</button>
            </div>
            )
        ) : (<>
            <header className="smww-form-header" data-atomic="molecule" data-atomic-name="form-header">
                <span className="smww-eyebrow" data-atomic="atom" data-atomic-name="eyebrow">
                    {content.eyebrow}
                </span>
                <h2 className="smww-heading" data-atomic="atom" data-atomic-name="heading">
                    {content.heading}
                </h2>
                <p className="smww-text smww-text--body" data-atomic="atom" data-atomic-name="body-text">
                    {content.description}
                </p>
            </header>
            <form className="smww-form" data-atomic="organism" data-atomic-name="quick-form">
                <div className={displayMode === "sidebar" ? "smww-grid smww-grid-side" : "smww-grid-modal"}>
                    {/* 1. First Name */}
                    <TextField
                        label="First Name"
                        id="dfname"
                        name="fname"
                        placeholder=" First Name"
                        value={formData.fname}
                        onChange={handleChange}
                        required
                        error={errors.fname}
                    />

                    {/* 2. Last Name */}
                    <TextField
                        label="Last Name"
                        id="dlname"
                        name="lname"
                        placeholder=" Last Name"
                        value={formData.lname}
                        onChange={handleChange}
                        required
                        error={errors.lname}
                    />

                    {/* 3. Email */}
                    <TextField
                        label="Email"
                        id="demail"
                        name="email"
                        type="email"
                        placeholder=" Email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        error={errors.email}
                    />

                    {/* 4. Phone */}
                    <PhoneField label="Phone" required error={errors.phone}>
                        <PhoneInput
                            international
                            countryCallingCodeEditable={false}
                            placeholder=" Phone"
                            className="smww-input"
                            value={formData.phone}
                            defaultCountry={formData.PrimaryPhoneCountry || 'US'}
                            onCountryChange={(country) => {
                            const callingCode = getCountryCallingCode(country);
                            setFormData((prev) => {
                                const newPhone =
                                !prev.phone || prev.phone.trim() === ''
                                    ? `+${callingCode}`
                                    : prev.phone;

                                return {
                                ...prev,
                                PrimaryPhoneCountry: country,
                                phone: newPhone,
                                };
                            });
                            }}
                            onChange={(phone) => {
                            console.log('Phone value updated to:', phone);
                                if (!phone) {
                                    console.log('Phone input cleared.');
                                    setFormData((prev) => ({
                                    ...prev,
                                    // phone: '',
                                    phone,
                                    }));
                                    return;
                                }

                                const digitsOnly = phone.replace(/\D/g, '');
                                let codeLength = 0;
                                if (formData.PrimaryPhoneCountry) {
                                    codeLength = getCountryCallingCode(formData.PrimaryPhoneCountry).length;
                                } else {
                                    const match = digitsOnly.match(/^\d{1,3}/);
                                    if (match) {
                                        codeLength = match[0].length;
                                    }
                                }
                                if (digitsOnly.length <= codeLength) {
                                    setFormData((prev) => ({
                                        ...prev,
                                        // phone: '',
                                        phone,
                                    }));
                                } else {
                                    setFormData((prev) => ({
                                        ...prev,
                                        phone: phone,
                                        // phone,
                                    }));
                                }
                            }}
                        />
                    </PhoneField>

                    {/* 5. Favorite Team or Club */}
                    <TextField
                        label="Favorite Team or Club"
                        id="fteam"
                        name="fteam"
                        placeholder=" Your Favorite Team"
                        value={formData.fteam}
                        onChange={handleChange}
                        required
                        error={errors.fteam}
                    />

                    {/* 6. City */}
                    <TextField
                        label="City"
                        id="fcity"
                        name="fcity"
                        placeholder=" City"
                        value={formData.fcity}
                        onChange={handleChange}
                        required
                        error={errors.fcity}
                    />

                    {/* 7. Country */}
                    <SelectField
                        label="Country"
                        id="fcountry"
                        name="fcountry"
                        value={formData.fcountry}
                        onChange={handleChange}
                        required
                        error={errors.fcountry}
                    >
                        <option value="">Select Country</option>
                        {Object.keys(countriesList).map(item => {
                            return <option key={countriesList[item].label} value={countriesList[item].label}>{countriesList[item].label}</option>
                        })}
                    </SelectField>

                    {/* 8. State */}
                    {formData.showStateField && (
                        <SelectField
                            label="State"
                            id="fstate"
                            name="fstate"
                            value={formData.fstate}
                            onChange={handleChange}
                            required
                            error={errors.fstate}
                        >
                            <option value="">Select a State or Province</option>
                            <option value="-">Outside the US or Canada</option>
                            {Object.keys(statelist).map(state => {
                                return <option key={statelist[state].name} value={statelist[state].name}>{statelist[state].name}</option>
                            })}
                        </SelectField>
                    )}

                    {/* 9. Province */}
                    {formData.showProvinceField && (
                        <TextField
                            label="Province"
                            name="fprovince"
                            placeholder=" Province"
                            value={formData.fprovince}
                            onChange={handleChange}
                            required
                            error={errors.fprovince}
                            inputClassName="smww-select"
                        />
                    )}
                </div>

                {/* SMS Sign-up Consent */}
                {!hasGeneralQuickForm(page) && (
                <ConsentCheckbox
                    label="Text me with news and offers"
                    id="text_with_me_news_offers"
                    name="field_text_me_with_news_offers"
                    defaultChecked={formData.field_text_me_with_news_offers === "1"}
                    onChange={handleChange}
                />
                )}

                {/* 10. CAPTCHA Wrapper */}
                <div className="smww-recaptcha-container">
                    {/* <div className="recaptcha" style={{transform:"scale(0.65, 0.85)", transformOrigin:"0 0", marginBottom:"0"}}> */}
                    <div 
                        style={displayMode === "sidebar" ? {transform: "scale(0.57)", transformOrigin: "0 0", marginBottom: "0", } : {} }
                    >
                        <ReCAPTCHA
                            // ref={(element) => {inputRefs.current['recaptchaRef'] = element;}}
                            ref={recaptchaRef}
                            sitekey={sitekey}
                            size="visible"
                            onChange={handleChange}
                            // required
                        />
                    </div>
                    {errors.recaptcha &&
                        <span className="sidebar-quickform-error sidebar-quickform-error-phone">
                        {errors.recaptcha}
                        </span>
                    }
                </div>

                {/* Form Actions (Button cta + placeholder loading state) */}
                {/* Dimmed while saving, label unchanged, as on the Drupal form (its Google Tag Manager trigger matches the button by its text) */}
                <button className="smww-btn smww-btn--cta" type="submit" onClick={handleSubmit} aria-disabled={saving ? "true" : undefined} style={saving ? { opacity: 0.7 } : undefined} data-atomic="atom" data-atomic-name="button">
                    {content.ctaText}
                </button>
                {errors.save &&
                    <span className="sidebar-quickform-error" role="alert">
                    {errors.save}
                    </span>
                }

                {/* Disclaimer Text */}
                <p className="smww-text smww-text--muted smww-form-footer">
                    {content.footer}
                </p>
            </form>
        </>)}
    </>
);

const handleClose = () => {
    setIsOpen(false);
    setFormData(initialForm);
    setErrors({});
};

    // return (
    //     <section className="smww-section course-sidebarform">
    //         <div className="smww-container">
    //             <div className="smww-surface">


    //             </div>
    //         </div>
    //     </section>
    // );
    if (displayMode === "sidebar") {
        return (
        <section className="smww-section course-sidebarform">
            <div className="smww-container">
                <div className="smww-surface">
                    {formContent}
                </div>
            </div>
        </section>
        );
    }

    return (
        <div className="course-sidebarform">
        <button onClick={() => setIsOpen(true)} className={className}>
            {content.ctaText}
        </button>

        {isOpen && (
            <div className="modal course-sideform-modal" onClick={handleClose}>
                <div className="modal-content course-sidebarform" onClick={(e) => e.stopPropagation()}>
                    <button onClick={handleClose} className="course-sidebarform-close">×</button>
                    <div className="smww-surface smww-modal-surface">
                        {formContent}
                    </div>
                </div>
            </div>
        )}
        </div>
    );

};
export default SidebarQuickForm;